import React, { useState } from "react";

const money = (v) => `${v > 0 ? "+" : ""}${Number(v || 0).toFixed(2)}$`;
const SEV = { CRITIQUE: "text-red-400", IMPORTANT: "text-orange-400", ATTENTION: "text-amber-300" };

const Kpi = ({ label, value, tone }) => (
  <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
    <div className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</div>
    <div className={`text-sm font-mono ${tone || ""}`}>{value}</div>
  </div>
);

const Conditions = ({ items, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid={testid}>
      <thead className="text-zinc-400 uppercase tracking-wider">
        <tr><th className="text-left py-1.5">Critère</th><th className="text-left">Condition</th>
          <th className="text-right">Trades</th><th className="text-right">Réussite</th>
          <th className="text-right">Écart</th><th className="text-right">% pertes</th><th className="text-right">P&L</th></tr>
      </thead>
      <tbody className="font-mono">
        {items.map((c, i) => (
          <tr key={i} className="border-t border-white/5">
            <td className="py-1.5 font-sans">{c.dimension}</td><td className="font-sans">{c.condition}</td>
            <td className="text-right">{c.trades}</td>
            <td className={`text-right ${c.winrate < 25 ? "text-red-400" : "text-orange-300"}`}>{c.winrate}%</td>
            <td className="text-right text-red-300">{c.gap > 0 ? "+" : ""}{c.gap} pts</td>
            <td className="text-right">{c.loss_share}%</td>
            <td className={`text-right ${c.net_pnl < 0 ? "text-red-400" : "text-emerald-400"}`}>{money(c.net_pnl)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StrategyCard = ({ s }) => {
  const k = s.kpis;
  const id = s.strategy.toLowerCase();
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-black/20" data-testid={`analytics-strategy-${id}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h4 className="text-base font-bold">{s.strategy}</h4>
        {!s.enough_data && <span className="text-[11px] text-amber-300">Moins de 10 trades — indicatif</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
        <Kpi label="Trades" value={k.trades} />
        <Kpi label="Réussite" value={`${k.winrate}%`} tone={k.winrate >= 50 ? "text-emerald-400" : "text-orange-300"} />
        <Kpi label="P. Factor" value={k.profit_factor == null ? "∞" : k.profit_factor} />
        <Kpi label="P&L net" value={money(k.net_pnl)} tone={k.net_pnl >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Kpi label="Gain moy." value={money(k.avg_win)} />
        <Kpi label="Perte moy." value={money(k.avg_loss)} />
        <Kpi label="Espérance" value={money(k.expectancy)} />
        <Kpi label="Max DD" value={money(-k.max_dd)} />
      </div>
      {!s.has_context && (
        <div className="text-[11px] text-zinc-500 mb-2">
          Contexte d'entrée (3MA, MTF, Stoch, ADX) disponible pour les nouveaux trades envoyés par l'EA v1.75+.
        </div>
      )}
      <div className="text-xs font-semibold text-amber-300 mb-1">Conditions des trades perdants</div>
      {s.losing_conditions.length ? <Conditions items={s.losing_conditions} testid={`analytics-conditions-${id}`} />
        : <div className="text-xs text-zinc-400 mb-2">Aucune condition perdante significative.</div>}
      {s.recommendations.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-semibold text-emerald-300 mb-1">Réglages conseillés</div>
          <ul className="space-y-2" data-testid={`analytics-recos-${id}`}>
            {s.recommendations.map((r, i) => (
              <li key={i} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 text-xs">
                <span className={`font-bold ${SEV[r.severity] || ""}`}>{r.severity}</span> — {r.text}
                {r.inputs.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.inputs.map((inp) => (
                      <code key={inp} className="px-1.5 py-0.5 rounded bg-cyan-900/30 text-cyan-300 text-[11px]">{inp}</code>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** Rapport par stratégie : KPIs, conditions perdantes, réglages d'inputs conseillés. */
export const AnalyticsStrategyReport = ({ strategies }) => {
  const [sel, setSel] = useState("ALL");
  if (!strategies?.length) return null;
  const shown = sel === "ALL" ? strategies : strategies.filter((s) => s.strategy === sel);
  return (
    <div className="mb-6 scroll-mt-24" data-testid="analytics-by-strategy">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-bold text-cyan-300">🎯 Analyse par stratégie</h3>
        <div className="flex gap-1 flex-wrap text-[11px]">
          {["ALL", ...strategies.map((s) => s.strategy)].map((n) => (
            <button key={n} onClick={() => setSel(n)} data-testid={`analytics-strategy-tab-${n.toLowerCase()}`}
                    className={`px-2 py-1 rounded ${sel === n ? "bg-cyan-600/30 text-cyan-200" : "text-zinc-400 hover:text-zinc-200"}`}>
              {n === "ALL" ? "Toutes" : n}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-4">{shown.map((s) => <StrategyCard key={s.strategy} s={s} />)}</div>
    </div>
  );
};
